import { ChakraProvider, extendTheme } from '@chakra-ui/react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect, createContext, useContext } from 'react'
import axios from 'axios'
import AdminLogin from './pages/AdminLogin'
import AdminLayout from './components/AdminLayout'
import AdminDashboard from './pages/AdminDashboard'
import AdminUsers from './pages/AdminUsers'
import AdminBooks from './pages/AdminBooks'
import AdminRevenue from './pages/AdminRevenue'
import AdminPricing from './pages/AdminPricing'
import AdminPlans from './pages/AdminPlans'

const theme = extendTheme({
  colors: {
    brand: { orange: '#E87300', darkBlue: '#0A1F44', gold: '#FFD700', green: '#1A6B2A', border: '#E2E2E2' }
  },
  fonts: { heading: `'Georgia', serif`, body: `'Inter', -apple-system, sans-serif` },
  styles: { global: { body: { bg: '#F7F7F5', color: '#0D0D0D' } } }
})

const AdminAuthContext = createContext()

function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null)
  const [loading, setLoading] = useState(true)
  const token = localStorage.getItem('wm_admin_token')

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      axios.get('/api/auth/me')
        .then(r => { if (r.data.role === 'admin') setAdmin(r.data); else logout() })
        .catch(logout)
        .finally(() => setLoading(false))
    } else { setLoading(false) }
  }, [])

  const login = async (email, password) => {
    const res = await axios.post('/api/auth/login', { email, password })
    if (res.data.user.role !== 'admin') throw new Error('Not an admin account')
    localStorage.setItem('wm_admin_token', res.data.token)
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`
    setAdmin(res.data.user)
  }

  const logout = () => {
    localStorage.removeItem('wm_admin_token')
    delete axios.defaults.headers.common['Authorization']
    setAdmin(null)
  }

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export function useAdminAuth() { return useContext(AdminAuthContext) }

function AdminRoute({ children }) {
  const { admin, loading } = useAdminAuth()
  if (loading) return null
  if (!admin) return <Navigate to="/login" replace />
  return children
}

export default function AdminApp() {
  return (
    <ChakraProvider theme={theme}>
      <AdminAuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<AdminLogin />} />
            <Route path="/" element={<AdminRoute><AdminLayout><AdminDashboard /></AdminLayout></AdminRoute>} />
            <Route path="/users" element={<AdminRoute><AdminLayout><AdminUsers /></AdminLayout></AdminRoute>} />
            <Route path="/books" element={<AdminRoute><AdminLayout><AdminBooks /></AdminLayout></AdminRoute>} />
            <Route path="/revenue" element={<AdminRoute><AdminLayout><AdminRevenue /></AdminLayout></AdminRoute>} />
            <Route path="/pricing" element={<AdminRoute><AdminLayout><AdminPricing /></AdminLayout></AdminRoute>} />
            <Route path="/plans" element={<AdminRoute><AdminLayout><AdminPlans /></AdminLayout></AdminRoute>} />
          </Routes>
        </Router>
      </AdminAuthProvider>
    </ChakraProvider>
  )
}
